// Phase 2: Message Signing + Verification

import { getSigningPrivateKey, saveSigningPrivateKey } from './signingKeyVault';

export async function getOrCreateSigningKeyPair(userId: string) {
  let privateKey = await getSigningPrivateKey(userId);

  if (privateKey) {
    const jwk = await crypto.subtle.exportKey('jwk', privateKey);
    delete jwk.d; delete jwk.dp; delete jwk.dq; delete jwk.qi;
    jwk.key_ops = ['verify'];
    const publicKey = await crypto.subtle.importKey('jwk', jwk, { name: 'RSA-PSS', hash: 'SHA-256' }, true, ['verify']);
    return { privateKey, publicKey };
  }

  const keyPair = await crypto.subtle.generateKey(
    {
      name: 'RSA-PSS',
      modulusLength: 2048,
      publicExponent: new Uint8Array([1, 0, 1]),
      hash: 'SHA-256',
    },
    true,
    ['sign', 'verify']
  );

  await saveSigningPrivateKey(userId, keyPair.privateKey);

  return keyPair;
}

export async function signMessage(userId: string, message: string): Promise<string> {
  const { privateKey } = await getOrCreateSigningKeyPair(userId);

  const encoder = new TextEncoder();
  const data = encoder.encode(message);

  const signature = await crypto.subtle.sign(
    {
      name: 'RSA-PSS',
      saltLength: 32,
    },
    privateKey,
    data
  );

  return btoa(String.fromCharCode(...new Uint8Array(signature)));
}

export async function verifyMessage(publicKey: CryptoKey, message: string, signatureBase64: string): Promise<boolean> {
  const encoder = new TextEncoder();
  const data = encoder.encode(message);

  const binary = atob(signatureBase64);
  const sigBytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) sigBytes[i] = binary.charCodeAt(i);

  return crypto.subtle.verify(
    {
      name: 'RSA-PSS',
      saltLength: 32,
    },
    publicKey,
    sigBytes,
    data
  );
}
